import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  FiHome, 
  FiSmartphone, 
  FiKey, 
  FiMenu, 
  FiLogOut,
  FiUser,
  FiBell,
  FiSettings,
  FiClipboard
} from 'react-icons/fi';

const Layout = () => {
  const [menuAbierto, setMenuAbierto] = useState(false);
  const { usuario, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef(null);

  // Cerrar menú cuando se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuAbierto(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const puedeVerAuditoria = usuario && [6, 7].includes(usuario.id);
  const menuItems = [
    { path: '/dashboard', icon: FiHome, label: 'Dashboard', visible: true },
    { path: '/dispositivos', icon: FiSmartphone, label: 'Dispositivos', visible: true },
    { path: '/licencias', icon: FiKey, label: 'Licencias', visible: true },
    { path: '/usuarios', icon: FiUser, label: 'Usuarios', visible: true },
    { path: '/auditoria', icon: FiClipboard, label: 'Auditoría', visible: puedeVerAuditoria },
  ];

  const navegarA = (path) => {
    navigate(path);
    setMenuAbierto(false);
  };

  const manejarLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Barra de navegación superior */}
      <nav className="bg-white shadow-sm border-b border-gray-200 fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo y navegación principal */}
            <div className="flex items-center">
              <div className="flex-shrink-0 flex items-center">
                <div className="h-8 w-8 bg-primary-500 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-white text-sm font-bold">GP</span>
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-gray-900">GoPhone MDM</h1>
                  <p className="text-xs text-gray-500">Control Simple</p>
                </div>
              </div>
              
              {/* Navegación principal - Desktop */}
              <div className="hidden md:ml-10 md:flex md:space-x-8">
                {menuItems.filter(item => item.visible).map((item) => {
                  const Icon = item.icon;
                  const activo = location.pathname === item.path;
                  
                  return (
                    <button
                      key={item.path}
                      onClick={() => navegarA(item.path)}
                      className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                        activo 
                          ? 'bg-primary-100 text-primary-600 border-b-2 border-primary-500' 
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <Icon size={16} className="mr-2" />
                      {item.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Lado derecho - Usuario y acciones */}
            <div className="flex items-center space-x-4">
              {/* Notificaciones */}
              <button className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 transition-colors duration-200">
                <FiBell size={20} />
              </button>
              
              {/* Configuración */}
              <button className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 transition-colors duration-200">
                <FiSettings size={20} />
              </button>

                             {/* Menú de usuario */}
               <div className="relative" ref={menuRef}>
                 <button
                   onClick={() => setMenuAbierto(!menuAbierto)}
                   className="flex items-center space-x-3 p-2 rounded-md hover:bg-gray-100 transition-colors duration-200"
                 >
                  <div className="h-8 w-8 bg-primary-100 rounded-full flex items-center justify-center">
                    <FiUser size={16} className="text-primary-600" />
                  </div>
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-medium text-gray-900">{usuario?.nombre_usuario}</p>
                    <p className="text-xs text-gray-500 capitalize">{usuario?.rol}</p>
                  </div>
                </button>

                {/* Menú desplegable */}
                {menuAbierto && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                    <button
                      onClick={manejarLogout}
                      className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors duration-200"
                    >
                      <FiLogOut size={16} className="mr-3" />
                      Cerrar Sesión
                    </button>
                  </div>
                )}
              </div>

              {/* Botón móvil */}
              <div className="md:hidden">
                <button
                  onClick={() => setMenuAbierto(!menuAbierto)}
                  className="p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                >
                  <FiMenu size={20} />
                </button>
              </div>
            </div>
          </div>

          {/* Menú móvil */}
          {menuAbierto && (
            <div className="md:hidden border-t border-gray-200 py-2">
              <div className="space-y-1">
                {menuItems.filter(item => item.visible).map((item) => {
                  const Icon = item.icon;
                  const activo = location.pathname === item.path;
                  
                  return (
                    <button
                      key={item.path}
                      onClick={() => navegarA(item.path)}
                      className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                        activo 
                          ? 'bg-primary-100 text-primary-600' 
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <Icon size={16} className="mr-3" />
                      {item.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Contenido principal */}
      <div className="pt-16">
        <main className="p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout; 